import Fuse from 'fuse.js';
import { getDistance } from 'geolib';

class LocationMatchingService {
  constructor() {
    this.successfulMatches = new Map();
    this.fuse = new Fuse([], {
      keys: ['name'],
      threshold: 0.3,
      distance: 100
    });
  }

  // Normalize and tokenize location text
  normalizeLocation(text) {
    if (!text) return [];
    return text.toLowerCase()
      .replace(/[.,#!$%^&*;:{}=_`~()]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 0);
  }

  // Calculate similarity between two locations using Jaccard similarity
  calculateSimilarity(location1, location2) {
    if (!location1 || !location2) return 0;
    
    const tokens1 = new Set(this.normalizeLocation(location1));
    const tokens2 = new Set(this.normalizeLocation(location2));
    
    const intersection = new Set([...tokens1].filter(x => tokens2.has(x)));
    const union = new Set([...tokens1, ...tokens2]);
    
    return intersection.size / union.size;
  }

  // Fuzzy search for locations
  fuzzySearch(locations, query) {
    this.fuse.setCollection(locations);
    return this.fuse.search(query);
  }

  // Calculate geographic distance between two points
  calculateDistance(coord1, coord2) {
    if (!coord1 || !coord2) return Infinity;
    return getDistance(coord1, coord2);
  }

  // Learn from successful matches
  learn(extractedLocation, googlePlace, wasMatch) {
    if (!extractedLocation || !googlePlace) return;
    
    if (wasMatch) {
      const key = extractedLocation.toLowerCase();
      const current = this.successfulMatches.get(key) || [];
      current.push(googlePlace);
      this.successfulMatches.set(key, current);
    }
  }

  // Get similar locations from learned patterns
  getSimilarLocations(location) {
    if (!location) return [];
    return this.successfulMatches.get(location.toLowerCase()) || [];
  }

  // Validate if coordinates are within reasonable ranges
  isValidCoordinate(lat, lng) {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  // Extract coordinates from text using various patterns
  extractCoordinates(text) {
    if (!text) return null;

    // Pattern for coordinates in format: 52.2297° N, 21.0122° E
    const coordPattern1 = /(\d+\.\d+)°\s*[NS],\s*(\d+\.\d+)°\s*[EW]/i;
    // Pattern for coordinates in format: 52.2297, 21.0122
    const coordPattern2 = /(\d+\.\d+),\s*(\d+\.\d+)/;
    // Pattern for coordinates in format: lat: 52.2297, lng: 21.0122
    const coordPattern3 = /lat:\s*(\d+\.\d+),\s*lng:\s*(\d+\.\d+)/i;

    const patterns = [coordPattern1, coordPattern2, coordPattern3];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        
        // Validate coordinates
        if (this.isValidCoordinate(lat, lng)) {
          return { latitude: lat, longitude: lng };
        }
      }
    }

    return null;
  }

  // Check if text is likely a location
  isLikelyLocation(text) {
    if (!text || text.length < 3) return false;

    // Common time-related phrases to exclude
    const timePhrases = [
      'hours ago', 'hour ago', 'days ago', 'day ago',
      'minutes ago', 'minute ago', 'seconds ago', 'second ago',
      'godzin temu', 'godziny temu', 'godzina temu',
      'dni temu', 'dzień temu', 'dnia temu',
      'minut temu', 'minuty temu', 'minuta temu',
      'sekund temu', 'sekundy temu', 'sekunda temu'
    ];

    // Check if text contains any time-related phrases
    if (timePhrases.some(phrase => text.toLowerCase().includes(phrase))) {
      return false;
    }

    // Check for common location indicators
    const locationIndicators = [
      'ul.', 'ulica', 'street', 'avenue', 'road',
      'al.', 'aleja', 'pl.', 'plac', 'square',
      'osiedle', 'os.', 'district', 'neighborhood',
      'warszawa', 'warsaw', 'kraków', 'krakow',
      'wrocław', 'wroclaw', 'poznań', 'poznan',
      'gdańsk', 'gdansk', 'łódź', 'lodz'
    ];

    return locationIndicators.some(indicator => 
      text.toLowerCase().includes(indicator.toLowerCase())
    );
  }

  // Get coordinates for a location using Google Places API
  async getCoordinatesForLocation(location) {
    if (!location || location.length < 3) {
      console.warn('Location too vague for geocoding:', location);
      return null;
    }

    // Check if the text is likely a location
    if (!this.isLikelyLocation(location)) {
      console.warn('Text does not appear to be a location:', location);
      return null;
    }

    try {
      // First try with the full location string
      let response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(location)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      let data = await response.json();

      // If no results and location contains a comma, try with just the first part
      if (!data.results?.length && location.includes(',')) {
        const firstPart = location.split(',')[0].trim();
        if (firstPart.length >= 3 && this.isLikelyLocation(firstPart)) {
          response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(firstPart)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          );
          data = await response.json();
        }
      }

      // If still no results, try with a more specific search
      if (!data.results?.length) {
        const enhancedLocation = `${location}, Poland`; // Add country for better results
        response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(enhancedLocation)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        );
        data = await response.json();
      }

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.geometry.location;
        
        // Extract address components
        const addressComponents = {};
        result.address_components.forEach(component => {
          component.types.forEach(type => {
            addressComponents[type] = {
              long_name: component.long_name,
              short_name: component.short_name
            };
          });
        });

        // Create a detailed location object
        return {
          latitude: lat,
          longitude: lng,
          formatted_address: result.formatted_address,
          location_type: result.geometry.location_type,
          bounds: result.geometry.bounds,
          viewport: result.geometry.viewport,
          address_components: addressComponents,
          place_id: result.place_id,
          is_partial_match: result.partial_match || false
        };
      } else {
        console.warn('No geocoding results found for location:', location);
        return null;
      }
    } catch (error) {
      console.error('Error getting coordinates:', error);
      return null;
    }
  }

  // Enhanced matchLocation method that uses the detailed location information
  async matchLocation(extractedLocation, googlePlace, maxDistance = 5000) {
    if (!extractedLocation || !googlePlace) return false;

    // First try to get coordinates from the extracted location
    let extractedCoords = this.extractCoordinates(extractedLocation);
    
    // If no coordinates found in text, try to get them from Google Places API
    if (!extractedCoords) {
      extractedCoords = await this.getCoordinatesForLocation(extractedLocation);
    }

    // If we have coordinates for both locations, use distance-based matching
    if (extractedCoords && googlePlace.coordinates) {
      const distance = this.calculateDistance(
        { latitude: extractedCoords.latitude, longitude: extractedCoords.longitude },
        { latitude: googlePlace.coordinates.latitude, longitude: googlePlace.coordinates.longitude }
      );
      
      // Learn from this match
      this.learn(extractedLocation, googlePlace, distance <= maxDistance);
      
      return distance <= maxDistance;
    }

    // If we have detailed location information, use it for better matching
    if (extractedCoords && extractedCoords.address_components) {
      const isInSameCountry = extractedCoords.address_components.country?.short_name === 
                            googlePlace.address_components?.country?.short_name;
      
      if (!isInSameCountry) {
        return false;
      }

      // If we have city information, check if it matches
      if (extractedCoords.address_components.locality && 
          googlePlace.address_components?.locality) {
        return extractedCoords.address_components.locality.long_name === 
               googlePlace.address_components.locality.long_name;
      }
    }

    // Fall back to text-based similarity if no detailed information available
    const similarity = this.calculateSimilarity(extractedLocation, googlePlace.name);
    this.learn(extractedLocation, googlePlace, similarity >= 0.6);
    return similarity >= 0.6;
  }

  // Extract location from text using patterns
  extractLocation(text) {
    if (!text) return null;

    // First try to find a city name
    const cityPattern = /(?:^|\s)(Wrocław|Warszawa|Kraków|Poznań|Gdańsk|Łódź|Wrocław|Warszawa|Krakow|Poznan|Gdansk|Lodz)(?:\s*,\s*[^,]+)?/i;
    const cityMatch = text.match(cityPattern);
    if (cityMatch) {
      return cityMatch[1].trim();
    }

    // Then try to find a street name followed by city
    const streetCityPattern = /([A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+)*)(?:\s*,\s*(?:Wrocław|Warszawa|Kraków|Poznań|Gdańsk|Łódź|Wrocław|Warszawa|Krakow|Poznan|Gdansk|Lodz))/i;
    const streetCityMatch = text.match(streetCityPattern);
    if (streetCityMatch) {
      return streetCityMatch[0].trim();
    }

    // Then try other patterns
    const locationPatterns = [
      // Street patterns with more context (e.g., "ul. Dokerska Kazanów Fabryczna, Wrocław")
      /(?:ul\.|ulica|al\.|aleja|pl\.|plac)\s+([^,]+?)(?:\s*,\s*[^,]+)?/i,
      
      // District patterns with full context (e.g., "mieszkania na wynajem w Odolany, Wola")
      /(?:mieszkania na wynajem|wynajem|w)\s+([^,]+?)(?:,\s*[^,]+)?(?:\s*,\s*Warszawa)?/i,
      
      // Title patterns (e.g., "Mieszkania na wynajem: Odolany, Wola")
      /(?:mieszkania na wynajem|wynajem):\s*([^|]+?)(?:\s*\|)/i,
      
      // Housing estate patterns with more context
      /(?:osiedle|os\.)\s+([^,]+?)(?:\s*,\s*[^,]+)?/i,
      
      // District + street patterns with more context
      /(?:w|w\s+okolicach|w\s+pobliżu)\s+([^,]+?)(?:\s+(?:ul\.|ulica|al\.|aleja|pl\.|plac)\s+[^,]+)?(?:\s*,\s*[^,]+)?/i,
      
      // General location patterns with more context
      /(?:w|w\s+okolicach|w\s+pobliżu|in|at|en)\s+([^,]+?)(?:\s*,\s*[^,]+)?/i,
      
      // Direct district names (e.g., "Odolany", "Stara Praga")
      /(?:^|\s)([A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+)*)(?:\s*,\s*[^,]+)?/i
    ];

    for (const pattern of locationPatterns) {
      const match = text.match(pattern);
      if (match) {
        const location = match[1].trim()
          .replace(/\s+/g, ' ')
          .replace(/^[,\s]+|[,\s]+$/g, '')
          .replace(/\s*,\s*/g, ', ');
        
        // Skip if the location is a date-related word
        const dateWords = ['may', 'days', 'ago', 'hours', 'hour', 'minutes', 'minute', 'seconds', 'second'];
        if (dateWords.some(word => location.toLowerCase().includes(word))) {
          continue;
        }
        
        // Only return if we have a meaningful location (more than 1 character)
        // and it's not just a single word that might be part of a sentence
        if (location.length > 1 && 
            (location.includes(',') || 
             location.includes(' ') || 
             /[A-ZĄĆĘŁŃÓŚŹŻ]/.test(location[0]))) {
          return location;
        }
      }
    }

    return null;
  }
}

// Export a singleton instance
export const locationMatchingService = new LocationMatchingService(); 